// engine/engine.js
// Yumiko DCS Engine — Dynamic Color Stack compositor.
// Pull-API, UI-agnostic. observer.js owns debounce/raf-throttle.
// Swatches are served cross-origin (Shopify CDN); they must respond with
// Access-Control-Allow-Origin so getImageData() can read pixels.

// Debug-aware logger. Uses the sidecar-installed window.__DCS_LOG__ when
// present (production: sidecar gates on ?dcs_debug / ?dcs_gen /
// ?dcs_swatch_test). Falls back to the same param rule if the wrapper is
// missing, so standalone loads (harness has its own always-verbose
// declaration ahead of import; anything else defaults silent unless the
// URL carries a bench param). console.error stays unconditional.
const LOG = (function () {
  if (typeof window === 'undefined') {
    return { log:()=>{}, info:()=>{}, warn:()=>{}, error:(...a)=>console.error(...a), table:()=>{} };
  }
  if (window.__DCS_LOG__) return window.__DCS_LOG__;
  const q = new URLSearchParams(window.location.search);
  const d = q.has('dcs_debug') || q.has('dcs_gen') || q.has('dcs_swatch_test');
  window.__DCS_LOG__ = {
    log:   d ? console.log.bind(console)  : function(){},
    info:  d ? console.info.bind(console) : function(){},
    warn:  d ? console.warn.bind(console) : function(){},
    error: console.error.bind(console),
    table: d && console.table ? console.table.bind(console) : function(){}
  };
  return window.__DCS_LOG__;
})();

const TRIM_SAMPLE_RECT = { x: 0.40, y: 0.45, w: 0.20, h: 0.10 }; // fractions of swatch
const HOLLOW_LUMA_THRESHOLD = 0.55; // L above → darken; below → lighten
const HOLLOW_LUMA_SHIFT     = 0.15; // absolute shift in HSL L, clamped to [0,1]
const HOLLOW_TRIM_FRACTION = 0.10; // drop top/bottom % by luminance
const HOLLOW_SAMPLE_STRIDE = 4;    // sample every Nth pixel

const styles = new Map();      // styleId -> manifest
const mounts = new Map();      // styleId -> { frontCanvas, backCanvas, masks, type }
const imageCache = new Map();  // url -> HTMLImageElement
const lastRender = new Map();  // styleId -> input hash

function loadImage(url) {
  if (imageCache.has(url)) return Promise.resolve(imageCache.get(url));
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imageCache.set(url, img); resolve(img); };
    img.onerror = () => reject(new Error(`DCS: failed to load ${url}`));
    img.src = url;
  });
}

// Fail-soft for optional mount keys — absence resolves to non-presence at the
// engine paint loop, which guards each optional region with `if (masks.X)`.
// Soft-key set:
//   hollow*  (hollow / hollowTop / hollowMiddle / hollowBottom) — apertures
//            are optional per zone+view (alicia precedent: only hollow-top-b
//            and hollow-bottom-f exist; the other two zone+view files don't).
//   trim     — trim is optional in shape: the structural binding ("main")
//            may be absent on a side for a deliberately asymmetric layout
//            (charlotte precedent: front has collar+zipper finish, no main
//            trim binding; back has both main and no-sleeve). Fail-soft
//            here lets `seed.trimDecoupled` declarations express per-side
//            asset presence without requiring all variants on all sides.
//   trimNoSleeve — same rationale; the no-sleeve segment may legitimately
//            be absent on a side for styles without that variant authored.
// Hard-fail set (a missing file IS a real bug worth surfacing):
//   body / bodyTop / bodyMiddle / bodyBottom — core body geometry.
//   trim cannot be hard-fail anymore (see above); body keys remain so.
//   zipper — a missing zipper on a front-zipper style is a loud failure
//            (Static-Floor leg 2). The frontOnly mechanism handles the
//            known-absent case (charlotte's b-side); a 404 on a side that
//            SHOULD exist must surface, not silently render.
async function loadMaskSet(maskUrls) {
  const entries = await Promise.all(
    Object.entries(maskUrls).map(async ([k, url]) => {
      try {
        return [k, await loadImage(url)];
      } catch (e) {
        if (k === 'hollow' || /^hollow[A-Z]/.test(k)) return null;
        if (k === 'trim' || k === 'trimNoSleeve') return null;
        throw e;
      }
    })
  );
  return Object.fromEntries(entries.filter(Boolean));
}

function hashInput(styleId, swatches) {
  return styleId + '|' + ['body','top','middle','bottom','sleeve','sleeveTrimMask','trim','zipper']
    .map(k => {
      const v = swatches[k];
      if (!v) return k + '=';
      if (typeof v === 'string') return k + '=' + v;
      return k + '=f:' + (v.f || '') + ',b:' + (v.b || '');
    })
    .join('|');
}

// --- Public API ---

function registerStyle(styleId, manifest) {
  // manifest shape:
  //   { type: 'single', sides: { front: { body, trim, hollow?, zipper? }, back: {...} } }
  //   { type: 'duo',    sides: { front: { bodyTop, bodyMiddle?, bodyBottom, sleeve?, trim, hollowTop?, hollowMiddle?, hollowBottom?, zipper? }, back: {...} } }
  // Each leaf is a URL string. hollow* keys are optional per zone+view; missing files silently
  // skip at mount and at render (engine fails soft per `docs/inside-color.md` and the
  // hollow-zoning principle in MANIFEST.md Conventions). Shadow_Map is intentionally
  // omitted (deprecated in v1).
  styles.set(styleId, manifest);
}

async function mount({ frontCanvas, backCanvas, styleId }) {
  const manifest = styles.get(styleId);
  if (!manifest) throw new Error(`DCS: style "${styleId}" not registered`);
  const masks = {
    front: await loadMaskSet(manifest.sides.front),
    back:  await loadMaskSet(manifest.sides.back),
  };
  mounts.set(styleId, { frontCanvas, backCanvas, masks, type: manifest.type });
}

function unmount(styleId) {
  const mounted = mounts.get(styleId);
  if (!mounted) return;
  // Drop mask images from the shared cache. Swatches stay — they may be reused.
  for (const side of ['front', 'back']) {
    for (const img of Object.values(mounted.masks[side] || {})) {
      imageCache.delete(img.src);
    }
  }
  mounts.delete(styleId);
  lastRender.delete(styleId);
}

async function render({ styleId, body, top, middle, bottom, sleeve, sleeveTrimMask, trim, zipper, force = false, soft = false }) {
  const mounted = mounts.get(styleId);
  if (!mounted) throw new Error(`DCS: style "${styleId}" not mounted`);

  const swatches = { body, top, middle, bottom, sleeve, sleeveTrimMask, trim, zipper };
  const hash = hashInput(styleId, swatches);
  if (!force && lastRender.get(styleId) === hash) return;

  // Sleeve modifier (work items 2 + 3, 2026-06-15 productionization of the
  // 2026-06-10 scratch). The bridge resolves the listener's sleeve length name
  // to a mask URL pair (sleeve: {f, b}) and the sleeve-hem trim to a mask URL
  // pair OR null when remove-trim is on (sleeveTrimMask: {f, b} | null). Engine
  // stays URL-agnostic — no hardcoded paths, no name → URL construction here.
  //
  // Load the side-appropriate sleeve and sleeve-hem trim masks now (the body-top
  // and trim swatch images they reveal through are loaded by the general imgs
  // preload below). Null out sleeve after extracting so sideUrls's
  // `pick(sleeve, s) || bodyTop` falls through to bodyTop — multi-body styles
  // with a mounted 'sleeve' region (none exist today) inherit the body-top
  // colour, which is the historical fall-through semantic for that region key.
  let sleeveMaskFront = null, sleeveMaskBack = null;
  let sleeveHemTrimMaskFront = null, sleeveHemTrimMaskBack = null;
  // Per-side presence-driven: pair may be partial (one side undefined). Each
  // side's load is gated by its own URL's truthiness, consistent with how
  // mount-time masks already ride per-side absence. Production bridge always
  // emits complete {f, b} pairs, so this is inert there; the relaxation
  // enables per-side sleeve subtraction in the harness (and is the same
  // change asymmetric-style support would need anyway).
  const hasSleeve = !!(sleeve && (sleeve.f || sleeve.b));
  if (hasSleeve) {
    if (sleeve.f) {
      try { sleeveMaskFront = await loadImage(sleeve.f); }
      catch (e) { LOG.warn(`DCS: sleeve mask ${sleeve.f} failed to load, skipping front`); }
    }
    if (sleeve.b) {
      try { sleeveMaskBack = await loadImage(sleeve.b); }
      catch (e) { LOG.warn(`DCS: sleeve mask ${sleeve.b} failed to load, skipping back`); }
    }
    sleeve = null;
  }
  if (sleeveTrimMask) {
    if (sleeveTrimMask.f) {
      try { sleeveHemTrimMaskFront = await loadImage(sleeveTrimMask.f); }
      catch (e) { LOG.warn(`DCS: sleeve-hem trim ${sleeveTrimMask.f} failed to load, skipping front`); }
    }
    if (sleeveTrimMask.b) {
      try { sleeveHemTrimMaskBack = await loadImage(sleeveTrimMask.b); }
      catch (e) { LOG.warn(`DCS: sleeve-hem trim ${sleeveTrimMask.b} failed to load, skipping back`); }
    }
  }

  // Multi-body when no single `body` mask is mounted (style has bodyTop/bodyMiddle/bodyBottom/sleeve instead).
  const isMultiBody = !mounted.masks.front.body;
  // Each zone arg is a URL string (used for both sides) or { f, b } (per-side, §2.1).
  const pick = (z, s) => (z && typeof z === 'object') ? z[s] : z;
  const sideUrls = (s) => {
    const bodyTop    = isMultiBody ? pick(top, s)    : pick(body, s);
    const bodyMiddle = isMultiBody ? pick(middle, s) : pick(body, s);
    const bodyBottom = isMultiBody ? pick(bottom, s) : pick(body, s);
    return {
      bodyTop,
      bodyMiddle,
      bodyBottom,
      sleeve: pick(sleeve, s) || bodyTop,
      trim:   pick(trim,   s) || bodyTop,
      // Per-region fall-through: zipper falls through to body (not bodyTop).
      // Multi-body styles with a zipper aren't yet specified — iter-2 will
      // make per-region fall-through targets first-class in the manifest.
      zipper: pick(zipper, s) || pick(body, s) || bodyTop,
      // Hollow is dispatched per-zone in the render loop; each hollow* mask reads
      // its adjacent body region's URL directly (urls.bodyTop / urls.bodyMiddle /
      // urls.bodyBottom). No explicit `hollow:` field needed in this resolved map.
    };
  };
  const resolved = { front: sideUrls('f'), back: sideUrls('b') };

  const allUrls = [...new Set(
    Object.values(resolved).flatMap(z => Object.values(z)).filter(Boolean)
  )];
  // Swatch preload. Hard-fail by default: a 404 here is a real commerce bug and
  // must propagate so the Static-Floor failsafe drops to native photography.
  // soft=true (capture / Share context) instead logs + skips the missing swatch;
  // the paint loop guards each swatchImg, so that region just does not paint and
  // the render still composes. The Share button can never take down a PDP.
  const imgs = Object.fromEntries(
    (await Promise.all(allUrls.map(async u => {
      try { return [u, await loadImage(u)]; }
      catch (e) {
        if (soft) { LOG.warn(`DCS: swatch ${u} failed to load, skipping (soft render)`); return null; }
        throw e;
      }
    }))).filter(Boolean)
  );

  for (const side of ['front', 'back']) {
    const canvas = side === 'front' ? mounted.frontCanvas : mounted.backCanvas;
    const masks  = mounted.masks[side];
    const urls   = resolved[side];
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 0. Sleeve modifier mask at Layer 0, painted with top color (urls.bodyTop =
    //    top swatch URL for duos via sideUrls). Side-appropriate mask selected from
    //    the per-view pair loaded above. Body and hollow layers paint over the
    //    sleeve where they overlap; sleeve-hem trim paints in Layer 4.
    const sleeveMask = side === 'front' ? sleeveMaskFront : sleeveMaskBack;
    if (sleeveMask) {
      paintImage(ctx, canvas, sleeveMask, imgs[urls.bodyTop]);
    }

    // 1. Hollow zones — per-zone luma-shift on the adjacent body region's color.
    //    Single archetypes have a `hollow` mask that inherits from the single body.
    //    Multi-body archetypes have `hollowTop` / `hollowMiddle` / `hollowBottom` —
    //    each reads its adjacent body zone's URL. Missing zone+view files were already
    //    dropped by loadMaskSet's fail-soft logic; the loop skips them by absence.
    for (const zoneKey of Object.keys(masks)) {
      if (!(zoneKey === 'hollow' || /^hollow[A-Z]/.test(zoneKey))) continue;
      const bodyUrlKey =
        // Unzoned 'hollow': fallback chain bodyBottom → bodyTop.
        // For single / single-with-front-zipper, urls.bodyBottom is undefined
        // (no `bottom` arg supplied; only the single body URL is bound, into
        // bodyTop via sideUrls's `bodyTop = pick(body, s)`). The chain falls
        // through to bodyTop, preserving the historical single-archetype
        // behaviour byte-identical.
        // For kayoko (dual-top-and-bottom, the only style with an unzoned
        // hollow on a multi-body archetype): urls.bodyBottom IS set because
        // bridge.js maps recipe region 'bottom-color' to mount-key
        // 'bodyBottom' and engine arg 'bottom'. MANIFEST.md declares
        // `hollow: fall_through: bottom-color` for this archetype, so the
        // hollow apertures (which live in/near the bottom region of the
        // garment) inherit their luma-shifted colour from bottom-color, not
        // from one of the two tops. The fallback chain is what makes that
        // MANIFEST contract executable without a new dispatch table.
        zoneKey === 'hollow'        ? (urls.bodyBottom ? 'bodyBottom' : 'bodyTop') :
        zoneKey === 'hollowTop'     ? 'bodyTop' :
        zoneKey === 'hollowMiddle'  ? 'bodyMiddle' :
        zoneKey === 'hollowBottom'  ? 'bodyBottom' :
        null;
      if (!bodyUrlKey) continue;
      const swatchImg = imgs[urls[bodyUrlKey]];
      if (!swatchImg) continue;
      paintFlat(ctx, canvas, masks[zoneKey], deriveHollowColor(swatchImg));
    }
    // 2. Body — single (one mask paints whole body) or multi (zones painted in canonical order).
    if (masks.body) {
      paintImage(ctx, canvas, masks.body, imgs[urls.bodyTop]);
    } else {
      for (const zone of ['bodyTop', 'bodyMiddle', 'bodyBottom', 'sleeve']) {
        if (masks[zone]) paintImage(ctx, canvas, masks[zone], imgs[urls[zone]]);
      }
    }
    // 3. Zipper — photographic reveal over body, beneath trim (front-only on Charlotte).
    if (masks.zipper) paintImage(ctx, canvas, masks.zipper, imgs[urls.zipper]);
    // 4. Trim — photographic reveal (last layer; overlays body-zone seams).
    if (masks.trim) paintImage(ctx, canvas, masks.trim, imgs[urls.trim]);
    // Trim decoupling: the no-sleeve segment paints only when no sleeve is
    // present (the sleeve covers that armhole binding); the sleeve-hem trim
    // paints when its mask was loaded (the bridge sets sleeveTrimMask to null
    // when remove-trim is on, which means the hem mask wasn't loaded and so
    // doesn't paint).
    if (masks.trimNoSleeve && !hasSleeve) {
      paintImage(ctx, canvas, masks.trimNoSleeve, imgs[urls.trim]);
    }
    const sleeveHemTrimMask = side === 'front' ? sleeveHemTrimMaskFront : sleeveHemTrimMaskBack;
    if (sleeveHemTrimMask) {
      paintImage(ctx, canvas, sleeveHemTrimMask, imgs[urls.trim]);
    }
  }

  lastRender.set(styleId, hash);
}

// --- Painters ---

function paintImage(ctx, canvas, maskImg, swatchImg) {
  // Fail-soft backstop: no-op when either image argument is missing. This catches
  // partial selections (the normal MTO flow — colours picked one at a time) where
  // an unpainted region's swatchImg is undefined because the bridge omitted that
  // region from args (its colour key didn't resolve, or the customer hasn't picked
  // it yet). The hollow dispatch already guards swatchImg before calling
  // deriveHollowColor; this is the same protection one layer down for every
  // photographic-reveal caller. Net effect: partial selections render the painted
  // zones and silently omit the rest, matching the asset-level fail-soft.
  if (!maskImg || !swatchImg) return;
  const off = makeOffscreen(canvas.width, canvas.height);
  const octx = off.getContext('2d');
  octx.drawImage(swatchImg, 0, 0, canvas.width, canvas.height);
  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
  ctx.drawImage(off, 0, 0);
}

function paintFlat(ctx, canvas, maskImg, cssColor) {
  const off = makeOffscreen(canvas.width, canvas.height);
  const octx = off.getContext('2d');
  octx.fillStyle = cssColor;
  octx.fillRect(0, 0, canvas.width, canvas.height);
  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
  ctx.drawImage(off, 0, 0);
}

function makeOffscreen(w, h) {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}

// --- Color derivation ---

function deriveHollowColor(swatchImg) {
  const { r, g, b } = trimmedMeanColor(swatchImg);
  const { h, s, l } = rgbToHsl(r, g, b);
  // Luminance-aware absolute shift: bright bottoms darken, dark bottoms lighten.
  // Hue and saturation preserved — hollow signals depth via lightness only.
  const shifted = l > HOLLOW_LUMA_THRESHOLD
    ? Math.max(0, l - HOLLOW_LUMA_SHIFT)
    : Math.min(1, l + HOLLOW_LUMA_SHIFT);
  const out = hslToRgb(h, s, shifted);
  return `rgb(${out.r|0}, ${out.g|0}, ${out.b|0})`;
}

function sampleTrimColor(swatchImg) {
  const w = swatchImg.naturalWidth, h = swatchImg.naturalHeight;
  const x  = Math.floor(w * TRIM_SAMPLE_RECT.x);
  const y  = Math.floor(h * TRIM_SAMPLE_RECT.y);
  const rw = Math.max(1, Math.floor(w * TRIM_SAMPLE_RECT.w));
  const rh = Math.max(1, Math.floor(h * TRIM_SAMPLE_RECT.h));
  const off = makeOffscreen(rw, rh);
  const octx = off.getContext('2d');
  octx.drawImage(swatchImg, x, y, rw, rh, 0, 0, rw, rh);
  const data = octx.getImageData(0, 0, rw, rh).data;
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] === 0) continue;
    r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
  }
  return n ? `rgb(${(r/n)|0}, ${(g/n)|0}, ${(b/n)|0})` : '#888';
}

function trimmedMeanColor(swatchImg) {
  const w = swatchImg.naturalWidth, h = swatchImg.naturalHeight;
  const off = makeOffscreen(w, h);
  off.getContext('2d').drawImage(swatchImg, 0, 0);
  const data = off.getContext('2d').getImageData(0, 0, w, h).data;
  const samples = [];
  const stride = HOLLOW_SAMPLE_STRIDE * 4;
  for (let i = 0; i < data.length; i += stride) {
    if (data[i + 3] === 0) continue;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    samples.push({ r, g, b, luma });
  }
  samples.sort((a, b) => a.luma - b.luma);
  const drop = Math.floor(samples.length * HOLLOW_TRIM_FRACTION);
  const trimmed = samples.slice(drop, samples.length - drop);
  let r = 0, g = 0, bb = 0;
  for (const s of trimmed) { r += s.r; g += s.g; bb += s.b; }
  const n = trimmed.length || 1;
  return { r: r / n, g: g / n, b: bb / n };
}

// --- HSL conversion ---

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h, s, l };
}

function hslToRgb(h, s, l) {
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  return { r: r * 255, g: g * 255, b: b * 255 };
}

// --- Dual export: ESM + global ---

const DCS = { registerStyle, mount, unmount, render };
if (typeof window !== 'undefined') window.DCS = DCS;
export default DCS;
export { registerStyle, mount, unmount, render };